import React, { useState, useEffect } from 'react';
import { api } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Activity,
  Database,
  Mail,
  Bell,
  Cpu,
  Server,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  FileDown,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatRomeDateTime } from '../lib/datetime';

const formatUptime = (seconds) => {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}g ${h}h ${m}min`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
};

const statusMeta = {
  healthy: { label: 'OK', icon: CheckCircle2, classes: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  degraded: { label: 'Degradato', icon: AlertTriangle, classes: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  critical: { label: 'Critico', icon: XCircle, classes: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
};

const KpiCard = ({ title, value, sub, icon: Icon, color = 'text-slate-900' }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4">
    <div className="flex items-start justify-between mb-1">
      <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{title}</span>
      <Icon className="w-4 h-4 text-slate-400" />
    </div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const SystemHealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const loadHealth = async (silent = false) => {
    try {
      if (!silent) setRefreshing(true);
      const { data: h } = await api.get('/admin/system-health');
      setData(h);
    } catch (e) {
      toast.error('Errore caricamento health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadHealth();
    const interval = setInterval(() => loadHealth(true), 30000);
    return () => clearInterval(interval);
  }, []);

  const handleTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Inserisci una email valida');
      return;
    }
    try {
      setSendingEmail(true);
      const { data: res } = await api.post('/admin/test-email', { to_email: testEmail });
      if (res.success) {
        toast.success(`Email di test inviata a ${testEmail}`);
      } else {
        toast.error('Invio fallito — controlla la sezione Email qui sotto');
      }
      // Refresh dei dati per vedere il nuovo last_success/failure
      loadHealth(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio test');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const response = await api.get('/admin/system-health-pdf', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `system_health_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report PDF scaricato');
    } catch (e) {
      toast.error('Errore download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }
  if (!data) return null;

  const overall = statusMeta[data.overall_status] || statusMeta.degraded;
  const OverallIcon = overall.icon;

  return (
    <div className="space-y-6" data-testid="system-health-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 flex items-center gap-2">
            <Activity className="w-7 h-7 text-blue-700" />
            Stato del sistema
          </h1>
          <p className="text-slate-500 mt-1">
            Generato il {formatRomeDateTime(data.generated_at)} · aggiornamento auto ogni 30s
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => loadHealth()}
            disabled={refreshing}
            data-testid="refresh-health-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <Button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            className="bg-blue-900 hover:bg-blue-800"
            data-testid="download-health-pdf"
          >
            {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />}
            Report PDF
          </Button>
        </div>
      </div>

      {/* Overall status banner */}
      <Card className={`border-2 ${overall.classes}`} data-testid="overall-status-card">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <span className={`shrink-0 w-3 h-3 mt-1.5 rounded-full ${overall.dot} animate-pulse`} />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <OverallIcon className="w-5 h-5" />
                <span className="font-bold text-lg">Stato complessivo: {overall.label}</span>
              </div>
              {data.issues.length > 0 ? (
                <ul className="text-sm mt-2 space-y-0.5">
                  {data.issues.map((issue, i) => (
                    <li key={i}>• {issue}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm">Tutti i sistemi funzionano regolarmente.</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 1) Database */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <Database className="w-4 h-4 text-blue-700" />
            Database (MongoDB)
            <Badge className={data.database.ping ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
              {data.database.ping ? 'Online' : 'Offline'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <KpiCard title="Dimensione dati" value={`${data.database.data_size_mb} MB`} icon={Database} />
            <KpiCard title="Storage" value={`${data.database.storage_size_mb} MB`} icon={Database} />
            <KpiCard title="Documenti" value={data.database.objects?.toLocaleString('it-IT') || '-'} icon={Database} />
            <KpiCard title="Collections" value={data.database.collections} icon={Database} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {data.database.counts && Object.entries(data.database.counts).map(([k, v]) => (
              <div key={k} className="flex justify-between bg-slate-50 px-3 py-1.5 rounded">
                <span className="text-slate-500">{k}</span>
                <span className="font-semibold text-slate-900">{v?.toLocaleString('it-IT') ?? '-'}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 2) SMTP */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-700" />
            Email (SMTP)
            <Badge className={data.smtp.configured ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
              {data.smtp.configured ? 'Configurato' : 'Non configurato'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard title="Successi 7gg" value={data.smtp.success_7d} icon={Mail} color="text-emerald-700" />
            <KpiCard title="Fallimenti 7gg" value={data.smtp.failure_7d} icon={Mail} color={data.smtp.failure_7d > 0 ? 'text-red-700' : 'text-slate-900'} />
            <KpiCard
              title="Ultimo invio OK"
              value={data.smtp.last_success ? formatRomeDateTime(data.smtp.last_success).split(',')[0] : '—'}
              sub={data.smtp.last_success ? formatRomeDateTime(data.smtp.last_success).split(',')[1] : null}
              icon={Mail}
            />
            <KpiCard
              title="Server"
              value={`${data.smtp.host}:${data.smtp.port}`}
              sub={data.smtp.from_email}
              icon={Mail}
            />
          </div>

          {data.smtp.last_failure && data.smtp.last_failure_meta?.error && (
            <Alert variant="destructive">
              <AlertDescription className="text-xs">
                <strong>Ultimo errore</strong> ({formatRomeDateTime(data.smtp.last_failure)}): {data.smtp.last_failure_meta.error}
              </AlertDescription>
            </Alert>
          )}

          {/* Test email */}
          <div className="bg-slate-50 rounded-lg p-3">
            <Label className="text-xs font-semibold text-slate-700 uppercase">Invia email di test</Label>
            <div className="flex gap-2 mt-2">
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="destinatario@dominio.com"
                data-testid="test-email-input"
              />
              <Button
                onClick={handleTestEmail}
                disabled={sendingEmail || !testEmail}
                className="bg-blue-900 hover:bg-blue-800"
                data-testid="test-email-button"
              >
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 3) Push Notifications */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <Bell className="w-4 h-4 text-blue-700" />
            Notifiche Push
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard title="Device registrati" value={data.push.registered_devices} icon={Bell} />
            <KpiCard title="Inviate 7gg" value={data.push.sent_7d} icon={Bell} color="text-emerald-700" />
            <KpiCard title="Fallite 7gg" value={data.push.failed_7d} icon={Bell} color={data.push.failed_7d > 0 ? 'text-red-700' : 'text-slate-900'} />
            <KpiCard
              title="Success rate"
              value={data.push.success_rate_7d !== null ? `${data.push.success_rate_7d}%` : '—'}
              icon={Bell}
              color={data.push.success_rate_7d !== null && data.push.success_rate_7d < 80 ? 'text-amber-700' : 'text-emerald-700'}
            />
          </div>
        </CardContent>
      </Card>

      {/* 4) API Stats */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <Cpu className="w-4 h-4 text-blue-700" />
            API (ultime 24 ore)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <KpiCard title="Richieste totali" value={data.api.total_requests_24h.toLocaleString('it-IT')} icon={Cpu} />
            <KpiCard title="Tempo medio" value={`${data.api.avg_response_ms} ms`} icon={Cpu} color={data.api.avg_response_ms > 1000 ? 'text-amber-700' : 'text-emerald-700'} />
            <KpiCard title="Errori 5xx" value={data.api.error_5xx_24h} icon={Cpu} color={data.api.error_5xx_24h > 0 ? 'text-red-700' : 'text-slate-900'} />
          </div>

          {data.api.top_endpoints.length > 0 && (
            <div>
              <Label className="text-xs font-semibold text-slate-700 uppercase">Top 5 endpoint</Label>
              <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="text-left p-2.5">Endpoint</th>
                      <th className="text-right p-2.5">Chiamate</th>
                      <th className="text-right p-2.5">Tempo medio</th>
                      <th className="text-right p-2.5">Errori</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.api.top_endpoints.map((ep, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-2.5 font-mono text-xs">{ep.path}</td>
                        <td className="p-2.5 text-right font-semibold">{ep.count}</td>
                        <td className="p-2.5 text-right">{ep.avg_ms} ms</td>
                        <td className={`p-2.5 text-right ${ep.errors > 0 ? 'text-red-700 font-bold' : 'text-slate-400'}`}>{ep.errors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 5) Service */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <Server className="w-4 h-4 text-blue-700" />
            Servizio
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard title="Versione" value={`v${data.service.version}`} icon={Server} />
            <KpiCard title="Uptime" value={formatUptime(data.service.uptime_seconds)} icon={Server} />
            <KpiCard
              title="Avviato il"
              value={formatRomeDateTime(data.service.started_at).split(',')[0]}
              sub={formatRomeDateTime(data.service.started_at).split(',')[1]}
              icon={Server}
            />
            <KpiCard
              title="Maintenance mode"
              value={data.service.maintenance_mode ? 'ATTIVO' : 'OFF'}
              icon={Server}
              color={data.service.maintenance_mode ? 'text-amber-700' : 'text-emerald-700'}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemHealthPage;
